import React, { useState, useEffect } from 'react';
import './App.css';
import SunCalc from 'suncalc';
import EarthModel from './EarthModel'; 
import SecretPage from './SecretPage'; 


// ==========================================
// 🛠️ 工具：公历转农历
// ==========================================
const getLunarDate = (date) => {
  const options = { calendar: 'chinese', month: 'long', day: 'numeric' };
  const lunarString = date.toLocaleDateString('zh-CN', options);
  const nums = ['〇', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
  
  const formatLunarDay = (dayStr) => {
    const day = parseInt(dayStr);
    if (isNaN(day)) return dayStr; 
    if (day <= 10) return '初' + nums[day];
    if (day < 20) return '十' + nums[day - 10];
    if (day === 20) return '二十';
    if (day < 30) return '廿' + nums[day - 20];
    if (day === 30) return '三十';
    return nums[day]; 
  };

  const monthMatch = lunarString.match(/(\d+|[一二三四五六七八九十冬腊正]+)月/);
  const dayMatch = lunarString.match(/(\d+)(日|$)/);

  let finalString = lunarString; 

  if (monthMatch && dayMatch) {
    let monthPart = monthMatch[1];
    let dayPart = dayMatch[1]; 

    if (!isNaN(monthPart)) {
       const m = parseInt(monthPart);
       if (m === 1) monthPart = '正';
       else if (m === 11) monthPart = '冬';
       else if (m === 12) monthPart = '腊';
       else if (m <= 10) monthPart = nums[m];
       else if (m > 10) monthPart = '十' + nums[m-10];
    }
    finalString = `${monthPart}月 · ${formatLunarDay(dayPart)}`;
  }
  return finalString;
};

const getWeekday = (date) => {
  const weekMap = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
  return weekMap[date.getDay()];
};

// ==========================================
// 🌿 节气与物候数据中心
// ==========================================
const SOLAR_TERMS_DATA = [
  { name: '小寒', date: '01-05', pentads: ['雁北乡', '鹊始巢', '雉始雊'] },
  { name: '大寒', date: '01-20', pentads: ['鸡乳', '征鸟厉疾', '水泽腹坚'] },
  { name: '立春', date: '02-04', pentads: ['东风解冻', '蛰虫始振', '鱼陟负冰'] },
  { name: '雨水', date: '02-19', pentads: ['獭祭鱼', '候雁北', '草木萌动'] },
  { name: '惊蛰', date: '03-05', pentads: ['桃始华', '仓庚鸣', '鹰化为鸠'] },
  { name: '春分', date: '03-20', pentads: ['玄鸟至', '雷乃发声', '始电'] },
  { name: '清明', date: '04-04', pentads: ['桐始华', '田鼠化为鴽', '虹始见'] },
  { name: '谷雨', date: '04-20', pentads: ['萍始生', '鸣鸠拂其羽', '戴胜降于桑'] },
  { name: '立夏', date: '05-05', pentads: ['蝼蝈鸣', '蚯蚓出', '王瓜生'] },
  { name: '小满', date: '05-21', pentads: ['苦菜秀', '靡草死', '麦秋至'] },
  { name: '芒种', date: '06-05', pentads: ['螳螂生', '鵙始鸣', '反舌无声'] },
  { name: '夏至', date: '06-21', pentads: ['鹿角解', '蜩始鸣', '半夏生'] },
  { name: '小暑', date: '07-07', pentads: ['温风至', '蟋蟀居宇', '鹰始挚'] },
  { name: '大暑', date: '07-22', pentads: ['腐草为萤', '土润溽暑', '大雨时行'] },
  { name: '立秋', date: '08-07', pentads: ['凉风至', '白露降', '寒蝉鸣'] },
  { name: '处暑', date: '08-23', pentads: ['鹰乃祭鸟', '天地始肃', '禾乃登'] },
  { name: '白露', date: '09-07', pentads: ['鸿雁来', '玄鸟归', '群鸟养羞'] },
  { name: '秋分', date: '09-23', pentads: ['雷始收声', '蛰虫坯户', '水始涸'] },
  { name: '寒露', date: '10-08', pentads: ['鸿雁来宾', '雀入大水为蛤', '菊有黄华'] },
  { name: '霜降', date: '10-23', pentads: ['豺乃祭兽', '草木黄落', '蛰虫咸俯'] },
  { name: '立冬', date: '11-07', pentads: ['水始冰', '地始冻', '雉入大水为蜃'] },
  { name: '小雪', date: '11-22', pentads: ['虹藏不见', '天气上升', '闭塞成冬'] },
  { name: '大雪', date: '12-07', pentads: ['鹖鴠不鸣', '虎始交', '荔挺生'] }, 
  { name: '冬至', date: '12-21', pentads: ['蚯蚓结', '麋角解', '水泉动'] },
];

const getSolarTermInfo = (date) => {
  const year = date.getFullYear();
  const currentMonthDay = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' }).replace('/', '-');
  
  let termIndex = -1;
  for (let i = 0; i < SOLAR_TERMS_DATA.length; i++) {
    if (currentMonthDay >= SOLAR_TERMS_DATA[i].date) {
      termIndex = i;
    } else {
      break;
    }
  }
  if (termIndex === -1) termIndex = SOLAR_TERMS_DATA.length - 1;

  const currentTerm = SOLAR_TERMS_DATA[termIndex];
  const termDate = new Date(year, parseInt(currentTerm.date.split('-')[0]) - 1, parseInt(currentTerm.date.split('-')[1]));
  
  if (termIndex === SOLAR_TERMS_DATA.length - 1 && date.getMonth() === 0) {
     termDate.setFullYear(year - 1);
  }
  
  const diffTime = Math.abs(date - termDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
  
  let pentadIndex = 0;
  if (diffDays >= 5) pentadIndex = 1;
  if (diffDays >= 10) pentadIndex = 2;
  pentadIndex = Math.min(pentadIndex, 2);

  return {
    term: currentTerm.name,
    pentad: currentTerm.pentads[pentadIndex]
  };
};

function App() {
  const [location, setLocation] = useState({ 
    latitude: 31.2304, 
    longitude: 121.4737,
    isDefault: true 
  });
  const [date, setDate] = useState(new Date());
  const [solarInfo, setSolarInfo] = useState({ term: '', pentad: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [secretCount, setSecretCount] = useState(0); 
  const [showSecretPage, setShowSecretPage] = useState(false); 

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            isDefault: false
          }),
        (error) => console.error("定位失败", error),
        { enableHighAccuracy: true }
      );
    }
  }, []); 

  useEffect(() => {
    setSolarInfo(getSolarTermInfo(date));
  }, [date]);

  const { latitude, longitude, isDefault } = location;
  const sunTimes = SunCalc.getTimes(date, latitude, longitude);
  const formatTime = (time) => time ? time.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }) : 'N/A';

  const handleTimeChange = (e) => {
    const minutes = parseInt(e.target.value);
    const newDate = new Date(date);
    newDate.setHours(Math.floor(minutes / 60));
    newDate.setMinutes(minutes % 60);
    setDate(newDate);
  };

  const handleSecretTrigger = () => {
    setSecretCount(prev => {
      const newCount = prev + 1;
      console.log(`Knock knock... ${newCount}`); 
      if (newCount >= 7) {
        setShowSecretPage(true); 
        return 0; 
      }
      return newCount;
    });
  };

  const getCurrentShiKe = () => {
    const SHI_CHEN = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'];
    const KE_SUFFIXES = ['时初', '初一刻', '初二刻', '初三刻', '时正', '正一刻', '正二刻', '正三刻'];
    
    const times = SunCalc.getTimes(date, latitude, longitude);
    const tomorrow = new Date(date);
    tomorrow.setDate(date.getDate() + 1);
    const timesTomorrow = SunCalc.getTimes(tomorrow, latitude, longitude);

    if (!times.sunrise || !times.sunset) return '计算中...';

    const dayDuration = times.sunset.getTime() - times.sunrise.getTime();
    const dayStep = dayDuration / 48;
    const nightDuration = timesTomorrow.sunrise.getTime() - times.sunset.getTime();
    const nightStep = nightDuration / 48;

    const currentTs = date.getTime();
    const sunriseTs = times.sunrise.getTime();
    const sunsetTs = times.sunset.getTime();

    let index = 0;

    if (currentTs >= sunriseTs && currentTs <= sunsetTs) {
      const diff = currentTs - sunriseTs;
      index = 28 + Math.floor(diff / dayStep);
    } else if (currentTs > sunsetTs) {
      const diff = currentTs - sunsetTs;
      index = 76 + Math.floor(diff / nightStep);
    } else {
      const diff = sunriseTs - currentTs;
      index = 28 - Math.ceil(diff / nightStep);
    }

    if (index < 0) index = 0; 
    if (index > 95) index = 95;

    const shiIndex = Math.floor(index / 8);
    const keIndex = index % 8;
    
    const shiName = SHI_CHEN[shiIndex];
    let displaySuffix = KE_SUFFIXES[keIndex];
    if (displaySuffix === '时初') displaySuffix = '初';
    if (displaySuffix === '时正') displaySuffix = '正';
    
    return `${shiName}时 · ${shiName}${displaySuffix}`;
  };

  return (
    <div className="App" style={{ 
      backgroundColor: '#000', color: 'white', height: '100vh', width: '100vw',
      position: 'relative', overflow: 'hidden', touchAction: 'none'  
    }}>
      
      {/* 🚪 秘密后台 */}
      {showSecretPage && (
        <SecretPage 
          onClose={() => setShowSecretPage(false)} 
          location={location} date={date} 
        />
      )}

      {/* 🔥 CSS 区域 */}
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Ma+Shan+Zheng&display=swap');
          
          @keyframes breathingGlow {
            0%, 100% {
              text-shadow: 0 0 2px rgba(255, 255, 255, 0.3);
              color: #e0e0e0; 
            }
            50% {
              text-shadow: 0 0 3px rgba(255, 255, 255, 0.8),
                           0 0 8px rgba(255, 255, 255, 0.5),
                           0 0 12px rgba(255, 255, 255, 0.2);
              color: #ffffff;
            }
          }

          .breathing-effect {
            animation: breathingGlow 5s ease-in-out infinite;
            color: #e0e0e0;
            border: 1px solid transparent; 
            -webkit-tap-highlight-color: transparent; 
            outline: none !important; 
            will-change: text-shadow; 
            transform: translateZ(0);
            -webkit-backface-visibility: hidden;
            user-select: none;
            -webkit-user-select: none;
          }
        `}
      </style>

      {/* 👈 左侧：天文数据仪表盘 (紧凑版) */}
      <div style={{
        position: 'absolute', 
        top: 'max(20px, env(safe-area-inset-top))', 
        left: isMobile ? '10px' : '20px', // ✅ 手机上更靠左，防止撞车
        // ❌ 删除了 width: 130px，让它自适应变窄
        zIndex: 10,
        background: 'rgba(30, 30, 30, 0.4)', 
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '12px 12px', // 内边距也稍微调小一点点
        border: '1px solid rgba(255,255,255,0.05)',
        boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
        display: 'flex', flexDirection: 'column', gap: '8px',
        textAlign: 'left'
      }}>
        {/* 日出 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}> {/* ✅ 改用 gap 控制距离 */}
          <span style={{ fontSize: '13px', color: '#ccc' }}>日出</span>
          <span style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 'bold', color: '#ff9cca' }}>
            {formatTime(sunTimes.sunrise)}
          </span>
        </div>
        
        {/* 日中 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#ccc' }}>日中</span>
          <span style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 'bold', color: '#ffd700' }}>
            {formatTime(sunTimes.solarNoon)}
          </span>
        </div>
        
        {/* 日落 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#ccc' }}>日落</span>
          <span style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 'bold', color: '#40a9ff' }}>
            {formatTime(sunTimes.sunset)}
          </span>
        </div>
        
        {/* 日背 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '13px', color: '#ccc' }}>日背</span>
          <span style={{ fontSize: '14px', fontFamily: 'monospace', fontWeight: 'bold', color: '#d3adf7' }}>
            {formatTime(sunTimes.nadir)}
          </span>
        </div>
      </div>


      {/* 👆 顶部中间：农历与星期 */}
      <div style={{ 
        position: 'absolute', top: 0, width: '100%', zIndex: 10, textAlign: 'center', pointerEvents: 'none', 
        paddingTop: 'max(20px, env(safe-area-inset-top))', 
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px'
      }}>
        
        {/* 农历 */}
        <div style={{ 
          fontSize: '32px', 
          fontFamily: '"Ma Shan Zheng", serif', 
          color: '#fff', 
          textShadow: '0 2px 4px rgba(0,0,0,0.5)',
          marginTop: '5px',
        }}>
          {getLunarDate(date)}
        </div>

        {/* 星期 */}
        <div style={{ 
          fontSize: '12px', 
          color: 'rgba(255,255,255,0.4)', 
          fontFamily: 'sans-serif',
          letterSpacing: '1px',
          marginBottom: '5px'
        }}>
          {getWeekday(date)}
        </div>

      </div>

      {/* 3D 地球 */}
      <div style={{ width: '100%', height: '100%' }}>
        <EarthModel latitude={latitude} longitude={longitude} date={date} />
      </div>

      {/* 底部控制面板 */}
      <div style={{
        position: 'absolute', 
        bottom: 'max(30px, env(safe-area-inset-bottom))', 
        left: '50%', transform: 'translateX(-50%)',
        width: '95%', maxWidth: '380px', 
        zIndex: 100,
      }}>
        
        {/* 🔮 玻璃面板容器 */}
        <div style={{
          background: 'rgba(30, 30, 30, 0.7)', 
          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', 
          borderRadius: '30px', border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.6)',
          padding: '20px 15px',
          display: 'flex', flexDirection: 'column', gap: '20px'
        }}>

          {/* 上半部分：左右护法 + 中间时间 */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            
            {/* 🏮 左护法：节气 */}
            <div 
              onClick={handleSecretTrigger}
              style={{
                background: 'rgba(255, 255, 255, 0.12)', 
                borderRadius: '16px',
                width: '52px', height: '110px',      
                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                paddingLeft: isMobile ? '30px' : '0px'  
            }}>
              <span style={{ 
                writingMode: 'vertical-rl', fontSize: '32px', fontWeight: '400',       
                lineHeight: '1', letterSpacing: '8px', color: '#fff', 
                fontFamily: '"Ma Shan Zheng", "STKaiti", "KaiTi", serif',
                textShadow: '0 2px 4px rgba(0,0,0,0.5)'
              }}>
                {solarInfo.term}
              </span>
            </div>

            {/* 🕰️ 中间：时间与日期 */}
            <div style={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              
              <div style={{ transform: 'translateY(-5px)' }}> 
                <div style={{ 
                  fontSize: '44px', fontWeight: 'bold', color: '#fff', 
                  fontFamily: 'monospace', letterSpacing: '2px', lineHeight: '1',
                  marginBottom: '4px'
                }}>
                  {date.getHours().toString().padStart(2, '0')}:{date.getMinutes().toString().padStart(2, '0')}
                </div>
                <div style={{ fontSize: '15px', fontWeight: '500', color: '#ccc', letterSpacing: '1px' }}>
                   {date.getFullYear()}年{date.getMonth()+1}月{date.getDate()}日
                </div>
              </div>

              {/* ✨ 时辰刻度 + 呼吸灯特效 */}
              <div 
                className="breathing-effect" 
                style={{ 
                  marginTop: '8px',
                  fontSize: '20px', 
                  fontFamily: '"Ma Shan Zheng", serif', 
                  letterSpacing: '2px',
                }}
              >
                {getCurrentShiKe()}
              </div>

            </div>

            {/* 🎋 右护法：物候 */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.06)',
              borderRadius: '16px',
              width: '52px', height: '110px',      
              display: 'flex', alignItems: 'center', justifyContent: 'center', 
              border: '1px solid rgba(255,255,255,0.05)',
              flexShrink: 0,
              paddingLeft: isMobile ? '18px' : '0px'
            }}>
              <span style={{ 
                writingMode: 'vertical-rl', fontSize: '18px', fontWeight: '400',       
                lineHeight: '1', letterSpacing: '6px', color: '#fff',           
                fontFamily: '"Ma Shan Zheng", "STKaiti", "KaiTi", serif'
              }}>
                {solarInfo.pentad}
              </span>
            </div>

          </div>

          {/* 下半部分：滑动条 */}
          <div>
            <input 
              type="range" min="0" max="1439" 
              value={date.getHours() * 60 + date.getMinutes()}
              onChange={handleTimeChange}
              style={{ width: '100%', cursor: 'pointer', accentColor: '#ff4d4f', height: '24px', display: 'block' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: '#666', padding: '0 4px' }}>
              <span>00:00</span>
              <span>12:00</span>
              <span>23:59</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default App;